// usePlaybookItemFocusCatalog.js

import { useEffect, useMemo, useState } from 'react';

// Services
import { subscribeToChecklistForField } from 'services/checklists/ChecklistService';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';

// Utilities
import { resolveTaskChecklistFieldName } from 'node/missions/mission/utils/workflowStepChecklistUtils';
import { WORKFLOW_STEP_TASK_TYPE_KEY } from 'node/missions/mission/utils/mintWorkflowStepTaskNodes';
import { getPlaybookItemFocusEntries } from 'node/missions/mission/focus/playbookItemFocusUtils';

/**
 * usePlaybookItemFocusCatalog
 *
 * Builds the flattened checklist-item path and live done/todo status
 * for the item-focus wizard.
 *
 * @param {object} params
 * @param {object|null} [params.missionRun]
 * @param {object|null} [params.template]
 * @param {Map<string, object>|null} [params.stepNodesByKey]
 * @returns {{ entries: object[], statusByKey: Map<string, object> }}
 */
export default function usePlaybookItemFocusCatalog({
  missionRun = null,
  template = null,
  stepNodesByKey = null,
} = {}) {
  const entries = useMemo(
    () => getPlaybookItemFocusEntries({
      missionRun,
      stepNodesByKey,
      template,
    }),
    [missionRun, stepNodesByKey, template],
  );

  const fieldName = useMemo(
    () => resolveTaskChecklistFieldName(
      getNodeType(WORKFLOW_STEP_TASK_TYPE_KEY) || null,
    ),
    [],
  );

  const stepTargets = useMemo(() => {
    const seen = new Set();
    const targets = [];

    entries.forEach((entry) => {
      const nodeKey = String(entry?.stepNode?.key || '').trim();
      const baseKey = String(entry?.stepNode?.baseKey || '').trim();
      if (!nodeKey || !baseKey || seen.has(nodeKey)) return;
      seen.add(nodeKey);
      targets.push({
        baseKey,
        nodeKey,
        stepKey: String(entry?.step?.task?.key || '').trim(),
      });
    });

    return targets;
  }, [entries]);

  const [statusByKey, setStatusByKey] = useState(() => new Map());

  useEffect(() => {
    if (!fieldName || stepTargets.length === 0) {
      setStatusByKey(new Map());
      return undefined;
    }

    let cancelled = false;
    const unsubscribers = stepTargets.map((target) => (
      subscribeToChecklistForField({
        baseKey: target.baseKey,
        fieldName,
        nodeKey: target.nodeKey,
      }, (row) => {
        if (cancelled) return;

        const items = Array.isArray(row?.sourceSnapshot?.items)
          ? row.sourceSnapshot.items
          : [];

        setStatusByKey((previous) => {
          const next = new Map(previous);
          items.forEach((item) => {
            const recipeKey = String(item?.templateKey || item?.key || '').trim();
            if (!recipeKey || !target.stepKey) return;
            next.set(`${target.stepKey}:${recipeKey}`, {
              done: String(item?.status || '').toLowerCase() === 'done',
              snapshotKey: String(item?.key || '').trim(),
            });
          });
          return next;
        });
      })
    ));

    return () => {
      cancelled = true;
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') unsubscribe();
      });
    };
  }, [fieldName, stepTargets]);

  return {
    entries,
    statusByKey,
  };
}
